import React, { useState, useEffect, useRef, useCallback, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/Sidebar/Sidebar';
import Topbar from '../../components/Topbar/Topbar';
import RightSidebar from '../../components/RightSidebar/RightSidebar';
import './Feed.css';

import API_URL from '../../config';
import EditorPreview from '../../components/EditorPreview/EditorPreview';

const DxfViewer = lazy(() => import('../../modules/dxf').then(m => ({ default: m.DxfViewer })));
const JbzViewer = lazy(() => import('../../modules/dxf').then(m => ({ default: m.JbzViewer })));
const DocPreview = lazy(() => import('../../components/DocPreview/DocPreview'));
const FullscreenViewer = lazy(() => import('../../components/FullscreenViewer/FullscreenViewer'));

// Wrapper to load DXF from URL for published posts
const PostDxfViewer = ({ url, height = 320, className = '' }) => {
  const [data, setData] = React.useState(null);
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(url);
        const text = await res.text();
        const DxfParser = (await import('dxf-parser')).default;
        const parser = new DxfParser();
        const parsed = parser.parseSync(text);
        if (!cancelled) setData(parsed);
      } catch (e) { console.error('DXF load error:', e); }
    })();
    return () => { cancelled = true; };
  }, [url]);
  if (!data) return <div className="feed-viewer-loading">DXF yükleniyor...</div>;
  return <DxfViewer dxfData={data} height={height} className={className} />;
};

// Published post file grid with click-to-preview
const PostFileGrid = ({ mediaUrls, apiUrl, downloadableIndexes = [], defaultPreviewIndex = null, postId, isOwner, token, editMode, autoPreview = false }) => {
  const [selectedIdx, setSelectedIdx] = React.useState(autoPreview ? (defaultPreviewIndex ?? 0) : defaultPreviewIndex);
  const [dlIndexes, setDlIndexes] = React.useState(downloadableIndexes);
  const [defPrevIdx, setDefPrevIdx] = React.useState(defaultPreviewIndex);
  const [fullscreenIdx, setFullscreenIdx] = React.useState(null);
  if (!mediaUrls?.length) return null;

  const imageUrls = mediaUrls.filter(u => /\.(jpg|jpeg|png|webp|gif)$/i.test(u));
  const selected = selectedIdx != null ? mediaUrls[selectedIdx] : null;
  const fullUrl = selected ? apiUrl + selected : '';
  const selectedDl = selectedIdx != null && dlIndexes.includes(selectedIdx);

  const saveSettings = async (updates) => {
    try {
      await fetch(`${apiUrl}/api/feed/posts/${postId}/settings`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });
    } catch { /* ignore */ }
  };

  const toggleDl = (idx) => {
    const next = dlIndexes.includes(idx) ? dlIndexes.filter(x => x !== idx) : [...dlIndexes, idx];
    setDlIndexes(next);
    saveSettings({ downloadableIndexes: next });
  };

  const toggleDefPrev = (idx) => {
    const next = defPrevIdx === idx ? null : idx;
    setDefPrevIdx(next);
    saveSettings({ defaultPreviewIndex: next });
  };

  return (
    <>
      {autoPreview && imageUrls.length > 1 && (
        <div className={`feed-explore-photo-grid grid-${Math.min(imageUrls.length, 4)}`}>
          {imageUrls.slice(0, 4).map((url, i) => (
            <div key={i} className="feed-explore-photo-cell">
              <img src={apiUrl + url} alt="" />
              {i === 3 && imageUrls.length > 4 && (
                <div className="feed-explore-photo-more">+{imageUrls.length - 4}</div>
              )}
            </div>
          ))}
        </div>
      )}
      {autoPreview && imageUrls.length === 1 && (
        <div className="feed-post-file-preview">
          <img src={apiUrl + imageUrls[0]} alt="" className="feed-post-file-preview-img" />
        </div>
      )}
      {autoPreview && imageUrls.length === 0 && selected && (
        <div className="feed-post-file-preview">
          {/\.dxf$/i.test(selected) && (
            <Suspense fallback={<div className="feed-viewer-loading">DXF yükleniyor...</div>}>
              <PostDxfViewer url={fullUrl} height={320} />
            </Suspense>
          )}
          {/\.jbz$/i.test(selected) && (
            <EditorPreview src={fullUrl} height={320} />
          )}
          {/\.(pdf|xlsx?|docx?)$/i.test(selected) && (
            <Suspense fallback={<div className="feed-viewer-loading">Doküman yükleniyor...</div>}>
              <DocPreview url={fullUrl} height={/\.pdf$/i.test(selected) ? 400 : /\.xlsx?$/i.test(selected) ? 320 : 300} />
            </Suspense>
          )}
        </div>
      )}
      {!autoPreview && <div className="feed-file-grid feed-file-grid-post">
        {mediaUrls.map((url, i) => {
          const fname = url.split('/').pop();
          const ext = fname.split('.').pop().toUpperCase();
          const isImage = /\.(jpg|jpeg|png|webp|gif)$/i.test(fname);
          const active = selectedIdx === i;
          const canDl = dlIndexes.includes(i);
          const isDefPrev = defPrevIdx === i;
          return (
            <div key={i} className={`feed-file-tile ${active ? 'selected' : ''} ${isDefPrev ? 'default-preview' : ''}`} onClick={() => setSelectedIdx(active ? null : i)}>
              {isImage ? (
                <img src={apiUrl + url} alt="" className="feed-file-tile-img" />
              ) : (
                <span className={`feed-file-tile-ext ext-${ext.toLowerCase()}`}>{ext}</span>
              )}
              {isOwner && editMode && (
                <>
                  <button className={`feed-file-tile-dl ${canDl ? 'allowed' : ''}`} title={canDl ? 'Indirilebilir' : 'Indirme kapali'} onClick={(e) => { e.stopPropagation(); toggleDl(i); }}>
                    {canDl ? (
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                    ) : (
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                    )}
                  </button>
                  <button className={`feed-file-tile-defprev ${isDefPrev ? 'active' : ''}`} title={isDefPrev ? 'Varsayılan ön izleme - tıkla kaldır' : 'Varsayılan ön izleme seç'} onClick={(e) => { e.stopPropagation(); toggleDefPrev(i); }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill={isDefPrev ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  </button>
                </>
              )}
              {!isOwner && canDl && <span className="feed-file-tile-dl-badge">&#8595;</span>}
              <span className="feed-file-tile-name">{fname}</span>
            </div>
          );
        })}
      </div>}
      {!autoPreview && selected && (
        <div className="feed-post-file-preview">
          {/\.dxf$/i.test(selected) && (
            <Suspense fallback={<div className="feed-viewer-loading">DXF yükleniyor...</div>}>
              <PostDxfViewer url={fullUrl} height={320} />
            </Suspense>
          )}
          {/\.jbz$/i.test(selected) && (
            <EditorPreview src={fullUrl} height={320} />
          )}
          {/\.(pdf|xlsx?|docx?)$/i.test(selected) && (
            <Suspense fallback={<div className="feed-viewer-loading">Doküman yükleniyor...</div>}>
              <DocPreview url={fullUrl} height={/\.pdf$/i.test(selected) ? 400 : /\.xlsx?$/i.test(selected) ? 320 : 300} />
            </Suspense>
          )}
          {/\.(jpg|jpeg|png|webp|gif)$/i.test(selected) && (
            <img src={fullUrl} alt="" className="feed-post-file-preview-img" />
          )}
          {!autoPreview && (
            <>
              <div className="feed-post-file-preview-bar">
                <span>{selected.split('/').pop()}</span>
                {(selectedDl || isOwner) && (
                  <a className="feed-doc-download-btn" href={fullUrl} download target="_blank" rel="noopener noreferrer">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                    Indir
                  </a>
                )}
                {!selectedDl && !isOwner && <span className="feed-dl-locked">Indirme kapali</span>}
              </div>
              <button className="feed-file-expand-btn" title="Tam ekran" onClick={() => setFullscreenIdx(selectedIdx)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></svg>
              </button>
            </>
          )}
        </div>
      )}
      {/* Fullscreen viewer modal */}
      {!autoPreview && fullscreenIdx != null && mediaUrls[fullscreenIdx] && (
        <Suspense fallback={null}>
          <FullscreenViewer
            url={apiUrl + mediaUrls[fullscreenIdx]}
            fileName={mediaUrls[fullscreenIdx].split('/').pop()}
            canDownload={dlIndexes.includes(fullscreenIdx) || isOwner}
            onClose={() => setFullscreenIdx(null)}
          />
        </Suspense>
      )}
    </>
  );
};


const Feed = ({ mode = 'feed' }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [posts, setPosts] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  // Anasayfa hızlı erişim şeridi — admin tarafından yönetilir
  const [homeShortcuts, setHomeShortcuts] = useState([]);
  useEffect(() => {
    fetch(`${API_URL}/api/system-settings/navigation`)
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        const items = d?.navigation?.homeShortcuts;
        if (Array.isArray(items)) setHomeShortcuts(items);
      })
      .catch(() => {});
  }, []);
  const [loading, setLoading] = useState(false);
  const activeTab = mode;
  const [newPostContent, setNewPostContent] = useState('');
  const [newPostMedia, setNewPostMedia] = useState([]);
  const [newPostType, setNewPostType] = useState('post');
  const [newPostTitle, setNewPostTitle] = useState('');
  const [newPostVisibility, setNewPostVisibility] = useState({ type: 'public', targets: [] });
  const [newPostFileAccess, setNewPostFileAccess] = useState({ type: 'public', targets: [] });
  const [visPickerOpen, setVisPickerOpen] = useState(false);
  const [accessPickerOpen, setAccessPickerOpen] = useState(false);
  const [pickerEntities, setPickerEntities] = useState([]);
  const [privateSearchQuery, setPrivateSearchQuery] = useState('');
  const [privateSearchResults, setPrivateSearchResults] = useState([]);
  const [privateSearchLoading, setPrivateSearchLoading] = useState(false);
  const privateSearchTimer = useRef(null);
  const visPickerRef = useRef(null);
  const accessPickerRef = useRef(null);
  const [newPostTags, setNewPostTags] = useState('');
  const [newPostMeta, setNewPostMeta] = useState({});
  const [pollOptions, setPollOptions] = useState(['', '']);
  const [posting, setPosting] = useState(false);
  const [filters, setFilters] = useState({ type: [], discipline: '', city: '', role: '' });
  const [dxfPreviewData, setDxfPreviewData] = useState(null);
  const [jbzPreviewFile, setJbzPreviewFile] = useState(null);
  const [docPreviewFiles, setDocPreviewFiles] = useState([]);
  const [previewFileIdx, setPreviewFileIdx] = useState(null);
  const [defaultPreviewIdx, setDefaultPreviewIdx] = useState(null);
  const [downloadableSet, setDownloadableSet] = useState(new Set());
  const [createFullscreen, setCreateFullscreen] = useState(false);
  const [dropActive, setDropActive] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [commentTexts, setCommentTexts] = useState({});
  const [openComments, setOpenComments] = useState({});
  const [postComments, setPostComments] = useState({});
  const [userSearch, setUserSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [theme] = useState(() => localStorage.getItem('projabze_theme') || 'dark');
  const [editingPostId, setEditingPostId] = useState(null);
  const [expandedPostId, setExpandedPostId] = useState(null);
  const fileInputRef = useRef(null);
  const observerRef = useRef(null);

  const token = localStorage.getItem('projabze_token');

  useEffect(() => {
    const stored = localStorage.getItem('projabze_user');
    if (stored) {
      setUser(JSON.parse(stored));
    } else {
      navigate('/');
    }
  }, [navigate]);

  const fetchPosts = useCallback(async (cursor = null, tab = activeTab) => {
    if (loading) return;
    setLoading(true);
    try {
      const endpoint = tab === 'explore' ? '/api/feed/explore' : '/api/feed';
      const params = new URLSearchParams({ limit: '15' });
      if (cursor) params.set('cursor', cursor);
      if (filters.type.length) params.set('type', filters.type.join(','));
      if (filters.discipline) params.set('discipline', filters.discipline);
      if (filters.city) params.set('city', filters.city);
      if (filters.role) params.set('role', filters.role);

      const res = await fetch(`${API_URL}${endpoint}?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (cursor) {
        setPosts(prev => [...prev, ...data.posts]);
      } else {
        setPosts(data.posts);
      }
      setNextCursor(data.nextCursor);
    } catch (err) {
      console.error('Feed fetch error:', err);
    }
    setLoading(false);
  }, [token, loading, activeTab, filters]);

  useEffect(() => {
    if (token) {
      fetchPosts(null, activeTab);
      fetchSuggestions();
      fetchPickerEntities();
    }
  }, [token, activeTab, filters]);

  const fetchSuggestions = async () => {
    try {
      const res = await fetch(`${API_URL}/api/social/users/suggestions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setSuggestions(data.suggestions || []);
    } catch (err) { /* ignore */ }
  };

  const fetchPickerEntities = async () => {
    try {
      const [grpRes, orgRes] = await Promise.all([
        fetch(`${API_URL}/api/groups`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/organizations`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      const grpData = await grpRes.json();
      const orgData = await orgRes.json();
      const entities = [
        ...(grpData.groups || []).map(g => ({ id: g.id, name: g.name, kind: 'group' })),
        ...(orgData.organizations || []).map(o => ({ id: o.id, name: o.name, kind: 'organization' }))
      ];
      setPickerEntities(entities);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (visPickerRef.current && !visPickerRef.current.contains(e.target)) setVisPickerOpen(false);
      if (accessPickerRef.current && !accessPickerRef.current.contains(e.target)) setAccessPickerOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handlePrivateUserSearch = (q) => {
    setPrivateSearchQuery(q);
    if (privateSearchTimer.current) clearTimeout(privateSearchTimer.current);
    if (!q.trim()) { setPrivateSearchResults([]); return; }
    privateSearchTimer.current = setTimeout(async () => {
      setPrivateSearchLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/social/users/search?q=${encodeURIComponent(q.trim())}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json();
        setPrivateSearchResults(data.users || []);
      } catch { setPrivateSearchResults([]); }
      setPrivateSearchLoading(false);
    }, 300);
  };

  // Infinite scroll
  const lastPostRef = useCallback(node => {
    if (loading) return;
    if (observerRef.current) observerRef.current.disconnect();
    observerRef.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && nextCursor) {
        fetchPosts(nextCursor);
      }
    });
    if (node) observerRef.current.observe(node);
  }, [loading, nextCursor, fetchPosts]);

  const handlePost = async () => {
    const hasContent = newPostContent.trim() || newPostMedia.length > 0;
    if (!hasContent || posting) return;
    setPosting(true);
    try {
      const formData = new FormData();
      formData.append('content', newPostContent);
      formData.append('visibility', JSON.stringify(newPostVisibility));
      formData.append('fileAccess', JSON.stringify(newPostFileAccess));
      formData.append('type', newPostType);
      if (newPostTitle.trim()) formData.append('title', newPostTitle.trim());
      if (newPostTags) formData.append('tags', JSON.stringify(newPostTags.split(',').map(t => t.trim()).filter(Boolean)));
      if (newPostType === 'regulation' && newPostMeta.sourceUrl) formData.append('sourceUrl', newPostMeta.sourceUrl);
      if (newPostType === 'project_showcase' && newPostMeta.projectMeta) formData.append('projectMeta', JSON.stringify(newPostMeta.projectMeta));
      if (newPostType === 'product_review' && newPostMeta.rating) formData.append('rating', newPostMeta.rating);
      if (newPostType === 'product_review' && newPostMeta.productName) formData.append('productName', newPostMeta.productName);
      if (newPostType === 'poll') formData.append('pollOptions', JSON.stringify(pollOptions.filter(o => o.trim())));
      formData.append('downloadableIndexes', JSON.stringify([...downloadableSet]));
      if (defaultPreviewIdx != null) formData.append('defaultPreviewIndex', String(defaultPreviewIdx));
      newPostMedia.forEach(f => formData.append('media', f));

      const res = await fetch(`${API_URL}/api/feed/posts`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      const data = await res.json();
      if (res.ok) {
        setPosts(prev => [data.post, ...prev]);
        setNewPostContent('');
        setNewPostMedia([]);
        setNewPostType('post');
        setNewPostTitle('');
        setNewPostVisibility({ type: 'public', targets: [] });
        setNewPostFileAccess({ type: 'public', targets: [] });
        setNewPostTags('');
        setNewPostMeta({});
        setPollOptions(['', '']);
        setDxfPreviewData(null);
        setJbzPreviewFile(null);
        setDocPreviewFiles([]);
        setPreviewFileIdx(null);
        setDefaultPreviewIdx(null);
        setDownloadableSet(new Set());
      } else {
        console.error('Post failed:', data.message);
        alert(data.message || 'Paylaşım oluşturulamadı.');
      }
    } catch (err) {
      console.error('Post error:', err);
      alert('Sunucuya ulaşılamadı.');
    }
    setPosting(false);
  };

  const handleLike = async (postId) => {
    try {
      const res = await fetch(`${API_URL}/api/feed/posts/${postId}/like`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setPosts(prev => prev.map(p =>
        p.id === postId
          ? { ...p, isLiked: data.liked, likeCount: p.likeCount + (data.liked ? 1 : -1) }
          : p
      ));
    } catch (err) { /* ignore */ }
  };

  const searchTimerRef = useRef(null);
  const POST_TYPE_MAP = {
    paylasim: 'post', soru: 'question', mevzuat: 'regulation', proje: 'project_showcase',
    uyari: 'field_warning', inceleme: 'product_review', anket: 'poll',
    post: 'post', question: 'question', regulation: 'regulation', project_showcase: 'project_showcase',
    field_warning: 'field_warning', product_review: 'product_review', poll: 'poll'
  };
  const POST_TYPE_LABELS = {
    post: 'Paylasim', question: 'Soru', regulation: 'Mevzuat', project_showcase: 'Proje',
    field_warning: 'Uyari', product_review: 'Inceleme', poll: 'Anket'
  };

  const handleUserSearch = (value) => {
    setUserSearch(value);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (!value.trim()) { setSearchResults([]); return; }

    const q = value.trim();

    // #etiket arama
    if (q.startsWith('#')) {
      const tag = q.slice(1).toLowerCase();
      if (!tag) { setSearchResults([]); return; }
      searchTimerRef.current = setTimeout(() => {
        setSearchLoading(true);
        const matched = posts
          .filter(p => p.tags?.some(t => t.toLowerCase().includes(tag)))
          .map(p => ({ _searchType: 'tag', ...p }));
        setSearchResults(matched.length > 0 ? matched : []);
        setSearchLoading(false);
      }, 150);
      return;
    }

    // &başlık arama
    if (q.startsWith('&')) {
      const titleQ = q.slice(1).trim();
      if (!titleQ) { setSearchResults([]); return; }
      searchTimerRef.current = setTimeout(async () => {
        setSearchLoading(true);
        try {
          const res = await fetch(`${API_URL}/api/feed/explore?titleSearch=${encodeURIComponent(titleQ)}&limit=10`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const data = await res.json();
          setSearchResults((data.posts || []).map(p => ({ _searchType: 'postTitle', ...p })));
        } catch { setSearchResults([]); }
        setSearchLoading(false);
      }, 300);
      return;
    }

    // @kullanıcı veya normal arama
    const userQ = q.startsWith('@') ? q.slice(1) : q;
    if (!userQ) { setSearchResults([]); return; }
    searchTimerRef.current = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/social/users/search?q=${encodeURIComponent(userQ)}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json();
        setSearchResults((data.users || []).map(u => ({ _searchType: 'user', ...u })));
      } catch { setSearchResults([]); }
      setSearchLoading(false);
    }, 300);
  };

  const handleFollowFromSearch = async (userId) => {
    try {
      await fetch(`${API_URL}/api/social/follows`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ followingId: userId })
      });
      setSearchResults(prev => prev.map(u => u.id === userId ? { ...u, isFollowing: true } : u));
      setSuggestions(prev => prev.filter(s => s.id !== userId));
    } catch { /* ignore */ }
  };

  const handleUnfollowFromSearch = async (userId) => {
    try {
      await fetch(`${API_URL}/api/social/unfollow/${userId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      setSearchResults(prev => prev.map(u => u.id === userId ? { ...u, isFollowing: false } : u));
    } catch { /* ignore */ }
  };

  const loadDxfPreview = async (file) => {
    try {
      const { parseDxfFile } = await import('../../modules/dxf');
      let fileToParse = file;
      if (/\.dwg$/i.test(file.name)) {
        const { convertDwgToDxfBrowser } = await import('../../utils/dwgConverter');
        let dxfText;
        try {
          dxfText = await convertDwgToDxfBrowser(file);
        } catch {
          const { convertDwgToDxf } = await import('../../services/projectApi');
          const blob = await convertDwgToDxf(file);
          dxfText = await blob.text();
        }
        fileToParse = new File([dxfText], file.name.replace(/\.dwg$/i, '.dxf'), { type: 'text/plain' });
      }
      setDxfPreviewData(await parseDxfFile(fileToParse));
    } catch (err) {
      console.error('DXF parse error:', err);
    }
  };

  const FEED_ACCEPTED_EXT = /\.(jpg|jpeg|png|webp|gif|dxf|dwg|jbz|pdf|xlsx|xls|doc|docx)$/i;

  const handleFeedFiles = (fileList) => {
    const files = Array.from(fileList || []).filter(f => FEED_ACCEPTED_EXT.test(f.name));
    if (!files.length) return;
    setNewPostMedia(prev => {
      const next = [...prev, ...files];
      if (previewFileIdx === null) setPreviewFileIdx(prev.length);
      return next;
    });
    files.forEach(f => {
      if (/\.(dxf|dwg)$/i.test(f.name)) loadDxfPreview(f);
      else if (/\.jbz$/i.test(f.name)) setJbzPreviewFile(f);
      else if (/\.(pdf|xlsx?|docx?)$/i.test(f.name)) setDocPreviewFiles(prev => [...prev, f]);
    });
  };

  const handleFeedDrop = (e) => {
    e.preventDefault();
    setDropActive(false);
    handleFeedFiles(e.dataTransfer.files);
  };

  const handlePollVote = async (postId, optionId) => {
    try {
      const res = await fetch(`${API_URL}/api/feed/posts/${postId}/poll-vote`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ optionId })
      });
      const data = await res.json();
      if (res.ok) {
        setPosts(prev => prev.map(p => p.id === postId ? { ...p, pollOptions: data.pollOptions } : p));
      }
    } catch { /* ignore */ }
  };

  const handleMarkSolution = async (postId, commentId) => {
    try {
      const res = await fetch(`${API_URL}/api/feed/posts/${postId}/comments/${commentId}/solution`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setPostComments(prev => ({
          ...prev,
          [postId]: (prev[postId] || []).map(c => ({ ...c, isSolution: c.id === commentId }))
        }));
        setPosts(prev => prev.map(p => p.id === postId ? { ...p, isSolved: true } : p));
      }
    } catch { /* ignore */ }
  };

  const handleFollow = async (userId) => {
    try {
      await fetch(`${API_URL}/api/social/follows`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ followingId: userId })
      });
      setSuggestions(prev => prev.filter(s => s.id !== userId));
    } catch (err) { /* ignore */ }
  };

  const toggleComments = async (postId) => {
    const isOpen = openComments[postId];
    if (!isOpen && !postComments[postId]) {
      try {
        const res = await fetch(`${API_URL}/api/feed/posts/${postId}/comments`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json();
        setPostComments(prev => ({ ...prev, [postId]: data.comments }));
      } catch (err) { /* ignore */ }
    }
    setOpenComments(prev => ({ ...prev, [postId]: !isOpen }));
  };

  const handleComment = async (postId) => {
    const text = commentTexts[postId];
    if (!text || !text.trim()) return;
    try {
      const res = await fetch(`${API_URL}/api/feed/posts/${postId}/comments`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ body: text })
      });
      const data = await res.json();
      if (res.ok) {
        setPostComments(prev => ({
          ...prev,
          [postId]: [...(prev[postId] || []), data.comment]
        }));
        setCommentTexts(prev => ({ ...prev, [postId]: '' }));
        setPosts(prev => prev.map(p =>
          p.id === postId ? { ...p, commentCount: p.commentCount + 1 } : p
        ));
      }
    } catch (err) { /* ignore */ }
  };

  const handleDeletePost = async (postId) => {
    try {
      const res = await fetch(`${API_URL}/api/feed/posts/${postId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setPosts(prev => prev.filter(p => p.id !== postId));
      }
    } catch (err) { /* ignore */ }
  };

  const formatTime = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Az önce';
    if (mins < 60) return `${mins}dk`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}sa`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}g`;
    return new Date(dateStr).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' });
  };

  if (!user) return null;
  const initials = (user.firstName?.[0] || '') + (user.lastName?.[0] || '');

  return (
    <div className={`feed-page ${theme} ${mode === 'explore' ? 'explore-mode' : ''}`}>
      <Topbar user={user} onLogout={() => {
        localStorage.removeItem('projabze_token');
        localStorage.removeItem('projabze_user');
        navigate('/');
      }}
        searchValue={userSearch}
        onSearchChange={handleUserSearch}
        searchPlaceholder="@kullanicilar, #etiketler, &basliklar"
      />
      {/* SIDEBAR */}
      <Sidebar activeId="home" user={user} onLogout={() => {
        localStorage.removeItem('projabze_token');
        localStorage.removeItem('projabze_user');
        navigate('/');
      }} />

      {/* MAIN FEED */}
      <main className="feed-main">

        {/* Search Results (input is in Topbar) */}
        {userSearch && (
          <div className="feed-user-search">
            {searchLoading && <div className="feed-search-loading">Aranıyor...</div>}
            {searchResults.length > 0 && (
              <div className="feed-search-results">
                {searchResults.map((item, idx) => {
                  if (item._searchType === 'user') return (
                    <div className="feed-search-result" key={item.id}>
                      <div className="feed-search-avatar" onClick={() => navigate(`/profile/${item.id}`)}>
                        {item.avatar ? <img src={API_URL + item.avatar} alt="" /> : (item.firstName?.[0] || '') + (item.lastName?.[0] || '')}
                      </div>
                      <div className="feed-search-info" onClick={() => navigate(`/profile/${item.id}`)}>
                        <span className="feed-search-name">{item.firstName} {item.lastName}</span>
                        {item.bio && <span className="feed-search-bio">{item.bio}</span>}
                        {item.city && <span className="feed-search-city">{item.city}</span>}
                      </div>
                      {item.isFollowing ? (
                        <button className="feed-search-following-btn" onClick={() => handleUnfollowFromSearch(item.id)}>Takip Ediliyor</button>
                      ) : (
                        <button className="feed-search-follow-btn" onClick={() => handleFollowFromSearch(item.id)}>Takip Et</button>
                      )}
                    </div>
                  );
                  if (item._searchType === 'tag') return (
                    <div className="feed-search-result feed-search-result-tag" key={item.id} onClick={() => { setFilters(f => ({ ...f, type: [] })); setUserSearch(''); setSearchResults([]); document.getElementById(`post-${item.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }); }}>
                      <div className="feed-search-tag-icon">#</div>
                      <div className="feed-search-info">
                        <span className="feed-search-name">{item.tags?.join(', ')}</span>
                        <span className="feed-search-bio">{item.content?.slice(0, 80)}{item.content?.length > 80 ? '...' : ''}</span>
                      </div>
                    </div>
                  );
                  if (item._searchType === 'postTitle') return (
                    <div className="feed-search-result feed-search-result-type" key={item.id} onClick={() => { setUserSearch(''); setSearchResults([]); document.getElementById(`post-${item.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }); }}>
                      <div className="feed-search-type-icon">&amp;</div>
                      <div className="feed-search-info">
                        <span className="feed-search-name">{item.title}</span>
                        <span className="feed-search-bio">{item.content?.slice(0, 80)}{item.content?.length > 80 ? '...' : ''}</span>
                      </div>
                    </div>
                  );
                  return null;
                })}
              </div>
            )}
            {!searchLoading && searchResults.length === 0 && (
              <div className="feed-search-empty">Sonuc bulunamadi.</div>
            )}
          </div>
        )}

        {/* Filter Bar */}
        <div className="feed-filters-bar">
          <div className="feed-filters-inner">
            <div className="feed-filter-group">
              <button className={`feed-filter-chip ${filters.type.length === 0 ? 'active' : ''}`} onClick={() => setFilters(f => ({ ...f, type: [] }))}>
                Tümü
              </button>
              {[
                { value: 'post', label: 'Paylaşım' },
                { value: 'question', label: 'Soru' },
                { value: 'regulation', label: 'Mevzuat' },
                { value: 'project_showcase', label: 'Proje' },
                { value: 'field_warning', label: 'Uyarı' },
                { value: 'product_review', label: 'İnceleme' },
                { value: 'poll', label: 'Anket' },
              ].map(t => {
                const isActive = filters.type.includes(t.value);
                return (
                  <button key={t.value} className={`feed-filter-chip ${isActive ? 'checked' : ''}`} onClick={() => setFilters(f => {
                    const arr = f.type.includes(t.value) ? f.type.filter(v => v !== t.value) : [...f.type, t.value];
                    return { ...f, type: arr };
                  })}>
                    {isActive && <svg className="feed-filter-check" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>}
                    {t.label}
                  </button>
                );
              })}
            </div>
            {filters.type.length > 0 && (
              <button className="feed-filter-clear-btn" onClick={() => setFilters({ type: [], discipline: '', city: '', role: '' })}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            )}
          </div>
        </div>

        {/* Create Post — only on feed mode */}
        {mode !== 'explore' && <div className="feed-create">
          <div className="feed-create-avatar">
            {user.avatar ? <img src={API_URL + user.avatar} alt="" /> : initials}
          </div>
          <div className="feed-create-body">
            <div className="feed-create-type-row">
              {[
                { id: 'post', label: 'Paylaşım' },
                { id: 'question', label: 'Soru' },
                { id: 'regulation', label: 'Mevzuat' },
                { id: 'project_showcase', label: 'Proje' },
                { id: 'field_warning', label: 'Uyarı' },
                { id: 'product_review', label: 'İnceleme' },
                { id: 'poll', label: 'Anket' },
              ].map(t => (
                <button key={t.id} className={`feed-type-chip ${newPostType === t.id ? 'active' : ''}`} onClick={() => setNewPostType(t.id)}>
                  {t.label}
                </button>
              ))}
            </div>
            <input
              className="feed-create-title"
              type="text"
              placeholder="Başlık (opsiyonel)"
              value={newPostTitle}
              onChange={e => setNewPostTitle(e.target.value)}
            />
            <textarea
              className="feed-create-input"
              placeholder={newPostType === 'question' ? 'Teknik sorunuzu yazın...' : newPostType === 'poll' ? 'Anket sorusunu yazın...' : newPostType === 'document' ? 'Doküman hakkında açıklama yazın...' : newPostType === 'news' ? 'Haber içeriğini yazın...' : newPostType === 'job' ? 'İş ilanı detaylarını yazın...' : newPostType === 'template' ? 'Şablon açıklamasını yazın...' : 'Ne düşünüyorsunuz? Proje paylaşımı yapın, soru sorun...'}
              value={newPostContent}
              onChange={e => setNewPostContent(e.target.value)}
              rows={3}
            />
            {/* Attached files grid + selected preview */}
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: 'none' }}
              multiple
              accept=".jpg,.jpeg,.png,.webp,.gif,.dxf,.dwg,.jbz,.pdf,.xlsx,.xls,.doc,.docx"
              onChange={e => { handleFeedFiles(e.target.files); e.target.value = ''; }}
            />
            {(() => {
              const selectedFile = previewFileIdx != null ? newPostMedia[previewFileIdx] : null;
              const selectedExt = selectedFile?.name.split('.').pop().toLowerCase();
              const isDxf = selectedExt === 'dxf';
              const isJbz = selectedExt === 'jbz';
              const isPdf = selectedExt === 'pdf';
              const isXls = selectedExt === 'xls' || selectedExt === 'xlsx';
              const isDoc = selectedExt === 'doc' || selectedExt === 'docx';
              const isImg = selectedFile && /\.(jpg|jpeg|png|webp|gif)$/i.test(selectedFile.name);
              if (newPostMedia.length === 0) {
                return (
                  <button
                    type="button"
                    className={`feed-file-add-btn ${dropActive ? 'drag' : ''}`}
                    onClick={() => fileInputRef.current?.click()}
                    onDragOver={e => { e.preventDefault(); setDropActive(true); }}
                    onDragLeave={() => setDropActive(false)}
                    onDrop={handleFeedDrop}
                    title="Dosya ekle (sürükle veya tıkla)"
                  >
                    <span className="feed-file-add-plus">+</span>
                    <span className="feed-file-add-label">Dosya ekle</span>
                  </button>
                );
              }
              return (
                <>
                  {/* File grid */}
                  <div className="feed-file-grid">
                    {newPostMedia.map((f, i) => {
                      const ext = f.name.split('.').pop().toUpperCase();
                      const isImage = /\.(jpg|jpeg|png|webp|gif)$/i.test(f.name);
                      const selected = previewFileIdx === i;
                      const isDefault = defaultPreviewIdx === i;
                      return (
                        <div key={i} className={`feed-file-tile ${selected ? 'selected' : ''} ${isDefault ? 'default-preview' : ''}`} onClick={() => setPreviewFileIdx(selected ? null : i)}>
                          {isImage ? (
                            <img src={URL.createObjectURL(f)} alt="" className="feed-file-tile-img" />
                          ) : (
                            <span className={`feed-file-tile-ext ext-${ext.toLowerCase()}`}>{ext}</span>
                          )}
                          <button className="feed-file-tile-remove" onClick={(e) => {
                            e.stopPropagation();
                            if (previewFileIdx === i) setPreviewFileIdx(null);
                            else if (previewFileIdx != null && previewFileIdx > i) setPreviewFileIdx(prev => prev - 1);
                            if (/\.dxf$/i.test(f.name)) setDxfPreviewData(null);
                            if (/\.jbz$/i.test(f.name)) setJbzPreviewFile(null);
                            setDocPreviewFiles(prev => prev.filter(ff => ff !== f));
                            setDownloadableSet(prev => { const n = new Set(prev); n.delete(i); return n; });
                            setNewPostMedia(prev => prev.filter(ff => ff !== f));
                          }}>×</button>
                          <button className={`feed-file-tile-dl ${downloadableSet.has(i) ? 'allowed' : ''}`} title={downloadableSet.has(i) ? 'İndirilebilir' : 'İndirme kapalı'} onClick={(e) => {
                            e.stopPropagation();
                            setDownloadableSet(prev => { const n = new Set(prev); if (n.has(i)) n.delete(i); else n.add(i); return n; });
                          }}>
                            {downloadableSet.has(i) ? (
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                            ) : (
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                            )}
                          </button>
                          <button className={`feed-file-tile-defprev ${defaultPreviewIdx === i ? 'active' : ''}`} title={defaultPreviewIdx === i ? 'Varsayılan ön izleme - tıkla kaldır' : 'Varsayılan ön izleme olarak seç'} onClick={(e) => {
                            e.stopPropagation();
                            setDefaultPreviewIdx(prev => prev === i ? null : i);
                          }}>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill={defaultPreviewIdx === i ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                          </button>
                          <span className="feed-file-tile-name">{f.name}</span>
                        </div>
                      );
                    })}
                    <div
                      className={`feed-file-tile feed-file-tile-add ${dropActive ? 'drag' : ''}`}
                      onClick={() => fileInputRef.current?.click()}
                      onDragOver={e => { e.preventDefault(); setDropActive(true); }}
                      onDragLeave={() => setDropActive(false)}
                      onDrop={handleFeedDrop}
                      title="Dosya ekle (sürükle veya tıkla)"
                    >
                      <span className="feed-file-tile-add-plus">+</span>
                    </div>
                  </div>
                  {/* Selected file full preview */}
                  {selectedFile && (
                    <div className="feed-create-file-preview">
                      {isDxf && dxfPreviewData && (
                        <Suspense fallback={<div className="feed-viewer-loading">DXF yükleniyor...</div>}>
                          <DxfViewer dxfData={dxfPreviewData} height={300} />
                        </Suspense>
                      )}
                      {isJbz && (
                        <Suspense fallback={<div className="feed-viewer-loading">JBZ yükleniyor...</div>}>
                          <JbzViewer file={selectedFile} height={300} />
                        </Suspense>
                      )}
                      {(isPdf || isXls || isDoc) && (
                        <Suspense fallback={<div className="feed-viewer-loading">Doküman yükleniyor...</div>}>
                          <DocPreview file={selectedFile} height={isPdf ? 360 : isXls ? 280 : 260} />
                        </Suspense>
                      )}
                      {isImg && <img src={URL.createObjectURL(selectedFile)} alt="" className="feed-create-img-preview" />}
                      <span className="feed-create-file-name">{selectedFile.name}</span>
                      <button className="feed-file-expand-btn" title="Tam ekran" onClick={() => setCreateFullscreen(true)}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></svg>
                      </button>
                    </div>
                  )}
                  {/* Fullscreen for create preview */}
                  {createFullscreen && selectedFile && (
                    <Suspense fallback={null}>
                      <FullscreenViewer
                        file={selectedFile}
                        fileName={selectedFile.name}
                        canDownload={true}
                        onClose={() => setCreateFullscreen(false)}
                      />
                    </Suspense>
                  )}
                </>
              );
            })()}
            {/* Type-specific fields */}
            {newPostType === 'regulation' && (
              <input className="feed-create-meta-input" type="url" placeholder="Resmi kaynak URL'si" value={newPostMeta.sourceUrl || ''} onChange={e => setNewPostMeta(m => ({ ...m, sourceUrl: e.target.value }))} />
            )}
            {newPostType === 'product_review' && (
              <div className="feed-create-meta-row">
                <input placeholder="Ürün adı" value={newPostMeta.productName || ''} onChange={e => setNewPostMeta(m => ({ ...m, productName: e.target.value }))} />
                <select value={newPostMeta.rating || ''} onChange={e => setNewPostMeta(m => ({ ...m, rating: e.target.value }))}>
                  <option value="">Puan</option>
                  {[1,2,3,4,5].map(n => <option key={n} value={n}>{'★'.repeat(n)}</option>)}
                </select>
              </div>
            )}
            {newPostType === 'poll' && (
              <div className="feed-create-poll">
                {pollOptions.map((opt, i) => (
                  <div key={i} className="feed-poll-option-input">
                    <input placeholder={`Seçenek ${i + 1}`} value={opt} onChange={e => setPollOptions(prev => prev.map((o, idx) => idx === i ? e.target.value : o))} />
                    {pollOptions.length > 2 && <button onClick={() => setPollOptions(prev => prev.filter((_, idx) => idx !== i))}>×</button>}
                  </div>
                ))}
                {pollOptions.length < 6 && <button className="feed-poll-add" onClick={() => setPollOptions(prev => [...prev, ''])}>+ Seçenek Ekle</button>}
              </div>
            )}
            <div className="feed-create-meta-row feed-create-privacy-row" style={{marginTop: '.5rem'}}>
              {/* Görünürlük */}
              <div className="feed-privacy-picker" ref={visPickerRef}>
                <button className="feed-privacy-btn" onClick={() => { setVisPickerOpen(v => !v); setAccessPickerOpen(false); setPrivateSearchQuery(''); setPrivateSearchResults([]); }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    {newPostVisibility.type === 'public' && <><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></>}
                    {newPostVisibility.type === 'followers' && <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></>}
                    {newPostVisibility.type === 'private' && <><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></>}
                    {newPostVisibility.type === 'groups' && <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/></>}
                  </svg>
                  Görünüm: {({public:'Herkes',followers:'Arkadaşlarım',private:'Özel',groups:'Grup/Org'})[newPostVisibility.type] || 'Herkes'}
                  {newPostVisibility.targets.length > 0 && <span className="feed-privacy-count">+{newPostVisibility.targets.length}</span>}
                </button>
                {visPickerOpen && (
                  <div className="feed-privacy-dropdown">
                    <div className="feed-privacy-options">
                      {[
                        { type: 'public', label: 'Herkes', desc: 'Tüm kullanıcılar görebilir' },
                        { type: 'followers', label: 'Arkadaşlarım', desc: 'Sadece takipçilerim' },
                        { type: 'private', label: 'Özel', desc: 'Seçtiğin kişiler görebilir' },
                        { type: 'groups', label: 'Grup / Organizasyon', desc: 'Seçtiğin gruplar' },
                      ].map(opt => (
                        <button key={opt.type} className={`feed-privacy-option ${newPostVisibility.type === opt.type ? 'active' : ''}`}
                          onClick={() => { setNewPostVisibility(v => ({ type: opt.type, targets: (opt.type === 'groups' || opt.type === 'private') ? v.targets : [] })); if (opt.type !== 'groups' && opt.type !== 'private') setVisPickerOpen(false); setPrivateSearchQuery(''); setPrivateSearchResults([]); }}>
                          <span className="feed-privacy-option-label">{opt.label}</span>
                          <span className="feed-privacy-option-desc">{opt.desc}</span>
                        </button>
                      ))}
                    </div>
                    {newPostVisibility.type === 'groups' && (
                      <div className="feed-privacy-entities">
                        {pickerEntities.map(ent => {
                          const sel = newPostVisibility.targets.includes(ent.id);
                          return (
                            <label key={ent.id} className="feed-privacy-entity">
                              <input type="checkbox" checked={sel} onChange={() => setNewPostVisibility(v => ({
                                ...v, targets: sel ? v.targets.filter(id => id !== ent.id) : [...v.targets, ent.id]
                              }))} />
                              <span className={`feed-privacy-entity-kind ${ent.kind}`}>{ent.kind === 'group' ? 'G' : 'O'}</span>
                              {ent.name}
                            </label>
                          );
                        })}
                        {pickerEntities.length === 0 && <div className="feed-privacy-empty">Henüz grup veya organizasyonunuz yok</div>}
                        <button className="feed-privacy-done" onClick={() => setVisPickerOpen(false)}>Tamam</button>
                      </div>
                    )}
                    {newPostVisibility.type === 'private' && (
                      <div className="feed-privacy-entities">
                        <input className="feed-privacy-search-input" placeholder="Kişi ara..." value={privateSearchQuery} onChange={e => handlePrivateUserSearch(e.target.value)} autoFocus />
                        {privateSearchLoading && <div className="feed-privacy-empty">Aranıyor...</div>}
                        {privateSearchResults.map(u => {
                          const sel = newPostVisibility.targets.includes(u.id);
                          return (
                            <label key={u.id} className="feed-privacy-entity">
                              <input type="checkbox" checked={sel} onChange={() => setNewPostVisibility(v => ({
                                ...v, targets: sel ? v.targets.filter(id => id !== u.id) : [...v.targets, u.id]
                              }))} />
                              <span className="feed-privacy-entity-avatar">{u.firstName?.[0]}{u.lastName?.[0]}</span>
                              {u.firstName} {u.lastName}
                            </label>
                          );
                        })}
                        {newPostVisibility.targets.length > 0 && (
                          <div className="feed-privacy-selected-count">{newPostVisibility.targets.length} kişi seçildi</div>
                        )}
                        <button className="feed-privacy-done" onClick={() => { setVisPickerOpen(false); setPrivateSearchQuery(''); setPrivateSearchResults([]); }}>Tamam</button>
                      </div>
                    )}
                  </div>
                )}
              </div>
              {/* Erişim (dosya) */}
              <div className="feed-privacy-picker" ref={accessPickerRef}>
                <button className="feed-privacy-btn feed-access-btn" onClick={() => { setAccessPickerOpen(v => !v); setVisPickerOpen(false); setPrivateSearchQuery(''); setPrivateSearchResults([]); }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>
                  </svg>
                  Erişim: {({public:'Herkes',followers:'Arkadaşlarım',private:'Özel',groups:'Grup/Org'})[newPostFileAccess.type] || 'Herkes'}
                  {newPostFileAccess.targets.length > 0 && <span className="feed-privacy-count">+{newPostFileAccess.targets.length}</span>}
                </button>
                {accessPickerOpen && (
                  <div className="feed-privacy-dropdown">
                    <div className="feed-privacy-hint">Dosya indirme, ön izleme ve yazdırma izni</div>
                    <div className="feed-privacy-options">
                      {[
                        { type: 'public', label: 'Herkes', desc: 'Tüm kullanıcılar erişebilir' },
                        { type: 'followers', label: 'Arkadaşlarım', desc: 'Sadece takipçilerim' },
                        { type: 'private', label: 'Özel', desc: 'Seçtiğin kişiler erişebilir' },
                        { type: 'groups', label: 'Grup / Organizasyon', desc: 'Seçtiğin gruplar' },
                      ].map(opt => (
                        <button key={opt.type} className={`feed-privacy-option ${newPostFileAccess.type === opt.type ? 'active' : ''}`}
                          onClick={() => { setNewPostFileAccess(v => ({ type: opt.type, targets: (opt.type === 'groups' || opt.type === 'private') ? v.targets : [] })); if (opt.type !== 'groups' && opt.type !== 'private') setAccessPickerOpen(false); setPrivateSearchQuery(''); setPrivateSearchResults([]); }}>
                          <span className="feed-privacy-option-label">{opt.label}</span>
                          <span className="feed-privacy-option-desc">{opt.desc}</span>
                        </button>
                      ))}
                    </div>
                    {newPostFileAccess.type === 'groups' && (
                      <div className="feed-privacy-entities">
                        {pickerEntities.map(ent => {
                          const sel = newPostFileAccess.targets.includes(ent.id);
                          return (
                            <label key={ent.id} className="feed-privacy-entity">
                              <input type="checkbox" checked={sel} onChange={() => setNewPostFileAccess(v => ({
                                ...v, targets: sel ? v.targets.filter(id => id !== ent.id) : [...v.targets, ent.id]
                              }))} />
                              <span className={`feed-privacy-entity-kind ${ent.kind}`}>{ent.kind === 'group' ? 'G' : 'O'}</span>
                              {ent.name}
                            </label>
                          );
                        })}
                        {pickerEntities.length === 0 && <div className="feed-privacy-empty">Henüz grup veya organizasyonunuz yok</div>}
                        <button className="feed-privacy-done" onClick={() => setAccessPickerOpen(false)}>Tamam</button>
                      </div>
                    )}
                    {newPostFileAccess.type === 'private' && (
                      <div className="feed-privacy-entities">
                        <input className="feed-privacy-search-input" placeholder="Kişi ara..." value={privateSearchQuery} onChange={e => handlePrivateUserSearch(e.target.value)} autoFocus />
                        {privateSearchLoading && <div className="feed-privacy-empty">Aranıyor...</div>}
                        {privateSearchResults.map(u => {
                          const sel = newPostFileAccess.targets.includes(u.id);
                          return (
                            <label key={u.id} className="feed-privacy-entity">
                              <input type="checkbox" checked={sel} onChange={() => setNewPostFileAccess(v => ({
                                ...v, targets: sel ? v.targets.filter(id => id !== u.id) : [...v.targets, u.id]
                              }))} />
                              <span className="feed-privacy-entity-avatar">{u.firstName?.[0]}{u.lastName?.[0]}</span>
                              {u.firstName} {u.lastName}
                            </label>
                          );
                        })}
                        {newPostFileAccess.targets.length > 0 && (
                          <div className="feed-privacy-selected-count">{newPostFileAccess.targets.length} kişi seçildi</div>
                        )}
                        <button className="feed-privacy-done" onClick={() => { setAccessPickerOpen(false); setPrivateSearchQuery(''); setPrivateSearchResults([]); }}>Tamam</button>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <button className="feed-create-btn" onClick={handlePost} disabled={posting || (!newPostContent.trim() && !newPostMedia.length)}>
                {posting ? 'Paylaşılıyor...' : 'Paylaş'}
              </button>
              <input placeholder="Etiketler (virgül ile)" value={newPostTags} onChange={e => setNewPostTags(e.target.value)} style={{flex:1}} />
            </div>
          </div>
        </div>}

        {/* Live Preview */}
        {mode !== 'explore' && newPostContent.trim() && (
          <div className="feed-preview-wrapper">
            <div className="feed-preview-label">Ön izleme</div>
            <div className="feed-post feed-post-preview">
              <div className="feed-post-header">
                <div className="feed-post-avatar">
                  {user.avatar ? <img src={API_URL + user.avatar} alt="" /> : initials}
                </div>
                <div className="feed-post-meta">
                  <span className="feed-post-author">
                    {user.firstName} {user.lastName}
                    {user.sectorRole && <span className="feed-role-badge">{({engineer_freelance:'Müellif',engineer_bureau:'Büro',inspector:'YD',authority:'Belediye',student:'Öğrenci',supplier:'Tedarikçi',employer:'İşveren'})[user.sectorRole] || ''}</span>}
                  </span>
                  <span className="feed-post-time">
                    Simdi
                    {newPostVisibility.type !== 'public' && <span className="feed-disc-badge">{({followers:'Arkadaşlar',private:'Özel',groups:'Grup/Org'})[newPostVisibility.type]}</span>}
                  </span>
                </div>
                {newPostTitle && <h3 className="feed-post-title">{newPostTitle}</h3>}
                {newPostType !== 'post' && (
                  <span className={`feed-header-type-chip type-${newPostType}`}>
                    <span className="feed-header-type-icon">
                      {newPostType === 'question' && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>}
                      {newPostType === 'regulation' && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>}
                      {newPostType === 'project_showcase' && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="9" y1="21" x2="9" y2="9"/></svg>}
                      {newPostType === 'field_warning' && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>}
                      {newPostType === 'product_review' && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>}
                      {newPostType === 'poll' && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>}
                      {newPostType === 'document' && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"/><polyline points="13 2 13 9 20 9"/></svg>}
                      {newPostType === 'news' && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M4 22h16a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v16a2 2 0 0 1-2 2zm0 0a2 2 0 0 1-2-2v-9c0-1.1.9-2 2-2h2"/><line x1="10" y1="6" x2="18" y2="6"/><line x1="10" y1="10" x2="18" y2="10"/><line x1="10" y1="14" x2="14" y2="14"/></svg>}
                      {newPostType === 'job' && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2" ry="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>}
                      {newPostType === 'template' && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>}
                    </span>
                    {({question:'Soru',regulation:'Mevzuat',project_showcase:'Proje',field_warning:'Uyari',product_review:'Inceleme',poll:'Anket',document:'Dokuman',news:'Haber',job:'Is Ilani',template:'Sablon'})[newPostType]}
                  </span>
                )}
              </div>
              <div className="feed-post-content">{newPostContent}</div>
              {newPostType === 'regulation' && newPostMeta.sourceUrl && (
                <a className="feed-source-link" href="#" onClick={e => e.preventDefault()}>Resmi Kaynak &rarr;</a>
              )}
              {newPostType === 'project_showcase' && newPostMeta.projectMeta && (
                <div className="feed-project-meta">
                  {newPostMeta.projectMeta.buildingType && <span>Yapi: {newPostMeta.projectMeta.buildingType}</span>}
                  {newPostMeta.projectMeta.power && <span>Guc: {newPostMeta.projectMeta.power} kW</span>}
                  {newPostMeta.projectMeta.floors && <span>Kat: {newPostMeta.projectMeta.floors}</span>}
                  {newPostMeta.projectMeta.city && <span>Sehir: {newPostMeta.projectMeta.city}</span>}
                </div>
              )}
              {newPostType === 'product_review' && (
                <div className="feed-review-meta">
                  {newPostMeta.productName && <span className="feed-product-name">{newPostMeta.productName}</span>}
                  {newPostMeta.rating && <span className="feed-rating">{'★'.repeat(parseInt(newPostMeta.rating))}{'☆'.repeat(5 - parseInt(newPostMeta.rating))}</span>}
                </div>
              )}
              {newPostType === 'poll' && pollOptions.some(o => o.trim()) && (
                <div className="feed-poll">
                  {pollOptions.filter(o => o.trim()).map((opt, i) => (
                    <button key={i} className="feed-poll-bar" style={{cursor:'default'}}>
                      <div className="feed-poll-fill" style={{ width: '0%' }}></div>
                      <span className="feed-poll-text">{opt}</span>
                      <span className="feed-poll-pct">0%</span>
                    </button>
                  ))}
                  <span className="feed-poll-total">0 oy</span>
                </div>
              )}
              {newPostTags && (
                <div className="feed-post-tags">{newPostTags.split(',').map(t => t.trim()).filter(Boolean).map((t, i) => <span key={i} className="feed-tag">#{t}</span>)}</div>
              )}
              {/* DXF/JBZ viewer in preview */}
              {dxfPreviewData && (
                <Suspense fallback={<div className="feed-viewer-loading">DXF yükleniyor...</div>}>
                  <DxfViewer dxfData={dxfPreviewData} height={280} className="feed-post-viewer" />
                </Suspense>
              )}
              {jbzPreviewFile && !dxfPreviewData && (
                <Suspense fallback={<div className="feed-viewer-loading">JBZ yükleniyor...</div>}>
                  <JbzViewer file={jbzPreviewFile} height={280} className="feed-post-viewer" />
                </Suspense>
              )}
              {/* Document previews in live preview */}
              {docPreviewFiles.map((f, i) => (
                <div key={i} className="feed-post-doc-viewer">
                  <Suspense fallback={<div className="feed-viewer-loading">Doküman yükleniyor...</div>}>
                    <DocPreview file={f} height={/\.pdf$/i.test(f.name) ? 350 : /\.xlsx?$/i.test(f.name) ? 280 : 260} className="feed-post-viewer" />
                  </Suspense>
                  <div className="feed-post-doc-bar">
                    <span className="feed-viewer-filename" style={{position:'static'}}>{f.name}</span>
                  </div>
                </div>
              ))}
              {/* Other media in live preview */}
              {newPostMedia.filter(f => !/\.(dxf|jbz|pdf|xlsx?|docx?)$/i.test(f.name)).length > 0 && (
                <div className="feed-post-media feed-preview-media">
                  {newPostMedia.filter(f => !/\.(dxf|jbz|pdf|xlsx?|docx?)$/i.test(f.name)).map((f, i) => {
                    const isImage = /\.(jpg|jpeg|png|webp|gif)$/i.test(f.name);
                    return isImage ? (
                      <img key={i} src={URL.createObjectURL(f)} alt="" className="feed-post-img" />
                    ) : (
                      <div key={i} className="feed-preview-file-card">
                        <span className="feed-preview-file-ext">{f.name.split('.').pop().toUpperCase()}</span>
                        <span className="feed-preview-file-name">{f.name}</span>
                      </div>
                    );
                  })}
                </div>
              )}
              <div className="feed-post-actions" style={{opacity: .4, pointerEvents: 'none'}}>
                <button className="feed-action-btn">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
                  <span>0</span>
                </button>
                <button className="feed-action-btn">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                  <span>0</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Posts */}
        <div className="feed-posts">
          {posts.map((post, idx) => {
            const isCompact = mode === 'explore' && expandedPostId !== post.id;
            const exploreImageCount = isCompact && post.mediaUrls?.length ? post.mediaUrls.filter(u => /\.(jpg|jpeg|png|webp|gif)$/i.test(u)).length : 0;
            const isSingleImageExplore = isCompact && exploreImageCount === 1;
            return (
            <div
              className={`feed-post ${isCompact ? 'feed-post-explore' : ''} ${isSingleImageExplore ? 'feed-post-explore-single' : ''}`}
              key={post.id}
              id={`post-${post.id}`}
              ref={idx === posts.length - 1 ? lastPostRef : null}
              onDoubleClick={mode === 'explore' ? () => setExpandedPostId(prev => prev === post.id ? null : post.id) : undefined}
              style={mode === 'explore' ? { cursor: 'pointer' } : undefined}
            >
              {!isCompact && <div className="feed-post-header">
                <div className="feed-post-avatar" onClick={() => post.author?.id && navigate(`/profile/${post.author.id}`)}>
                  {post.author?.avatar ? <img src={API_URL + post.author.avatar} alt="" /> : (post.author?.firstName?.[0] || '') + (post.author?.lastName?.[0] || '')}
                </div>
                <div className="feed-post-meta">
                  <span className="feed-post-author" onClick={() => post.author?.id && navigate(`/profile/${post.author.id}`)}>
                    {post.author?.firstName} {post.author?.lastName}
                    {post.author?.sectorRole && <span className="feed-role-badge">{({engineer_freelance:'Müellif',engineer_bureau:'Büro',inspector:'YD',authority:'Belediye',student:'Öğrenci',supplier:'Tedarikçi',employer:'İşveren'})[post.author.sectorRole] || ''}</span>}
                    {post.author?.level && post.author.level !== 'acemi' && <span className="feed-level-badge">{post.author.level}</span>}
                  </span>
                  <span className="feed-post-time">
                    {formatTime(post.createdAt)}
                    {post.discipline && <span className="feed-disc-badge">{({electrical:'Kuvvetli Akim',fire:'Yangin',weak:'Zayif Akim'})[post.discipline]}</span>}
                  </span>
                </div>
                {post.title && <h3 className="feed-post-title">{post.title}</h3>}
                <span className={`feed-header-type-chip type-${post.type || 'post'}`}>
                  <span className="feed-header-type-icon">
                    {(!post.type || post.type === 'post') && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>}
                    {post.type === 'question' && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>}
                    {post.type === 'regulation' && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>}
                    {post.type === 'project_showcase' && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="9" y1="21" x2="9" y2="9"/></svg>}
                    {post.type === 'field_warning' && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>}
                    {post.type === 'product_review' && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>}
                    {post.type === 'poll' && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>}
                    {post.type === 'media' && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>}
                    {post.type === 'document' && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"/><polyline points="13 2 13 9 20 9"/></svg>}
                    {post.type === 'news' && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M4 22h16a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v16a2 2 0 0 1-2 2zm0 0a2 2 0 0 1-2-2v-9c0-1.1.9-2 2-2h2"/><line x1="10" y1="6" x2="18" y2="6"/><line x1="10" y1="10" x2="18" y2="10"/><line x1="10" y1="14" x2="14" y2="14"/></svg>}
                    {post.type === 'job' && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2" ry="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>}
                    {post.type === 'template' && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>}
                  </span>
                  {({post:'Paylasim',question:'Soru',regulation:'Mevzuat',project_showcase:'Proje',field_warning:'Uyari',product_review:'Inceleme',poll:'Anket',media:'Medya',document:'Dokuman',news:'Haber',job:'Is Ilani',template:'Sablon',link:'Link'})[post.type || 'post']}
                  {post.isSolved && <span className="feed-solved-pip">&#10003;</span>}
                </span>
                {post.userId === user.id && (
                  <>
                    <button className={`feed-post-edit ${editingPostId === post.id ? 'active' : ''}`} title={editingPostId === post.id ? 'Duzenlemeyi kapat' : 'Duzenle'} onClick={() => setEditingPostId(editingPostId === post.id ? null : post.id)}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                    </button>
                    <button className="feed-post-delete" onClick={() => handleDeletePost(post.id)}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                    </button>
                  </>
                )}
              </div>}
              {isSingleImageExplore ? (
                <div className={`feed-explore-single-layout${post.content?.trim() ? ' has-text' : ''}`}>
                  <div className="feed-explore-single-img">
                    <img src={API_URL + post.mediaUrls.find(u => /\.(jpg|jpeg|png|webp|gif)$/i.test(u))} alt="" />
                  </div>
                  {post.content?.trim() && <div className="feed-explore-single-text">{post.content}</div>}
                </div>
              ) : (
                <>
                  {post.content?.trim() && <div className="feed-post-content">{post.content}</div>}
                  {/* Type-specific rendering */}
                  {!isCompact && post.type === 'regulation' && post.sourceUrl && (
                    <a className="feed-source-link" href={post.sourceUrl} target="_blank" rel="noopener noreferrer">Resmi Kaynak &rarr;</a>
                  )}
                  {post.type === 'project_showcase' && post.projectMeta && (
                    <div className="feed-project-meta">
                      {post.projectMeta.buildingType && <span>Yapi: {post.projectMeta.buildingType}</span>}
                      {post.projectMeta.power && <span>Guc: {post.projectMeta.power} kW</span>}
                      {post.projectMeta.floors && <span>Kat: {post.projectMeta.floors}</span>}
                      {post.projectMeta.city && <span>Sehir: {post.projectMeta.city}</span>}
                    </div>
                  )}
                  {post.type === 'product_review' && (
                    <div className="feed-review-meta">
                      {post.productName && <span className="feed-product-name">{post.productName}</span>}
                      {post.rating && <span className="feed-rating">{'★'.repeat(post.rating)}{'☆'.repeat(5 - post.rating)}</span>}
                    </div>
                  )}
                  {post.type === 'poll' && post.pollOptions && (
                    <div className="feed-poll">
                      {post.pollOptions.map(opt => {
                        const totalVotes = post.pollOptions.reduce((sum, o) => sum + (o.votes || 0), 0);
                        const pct = totalVotes > 0 ? Math.round((opt.votes || 0) / totalVotes * 100) : 0;
                        return (
                          <button key={opt.id} className="feed-poll-bar" onClick={() => handlePollVote(post.id, opt.id)}>
                            <div className="feed-poll-fill" style={{ width: `${pct}%` }}></div>
                            <span className="feed-poll-text">{opt.text}</span>
                            <span className="feed-poll-pct">{pct}%</span>
                          </button>
                        );
                      })}
                      <span className="feed-poll-total">{post.pollOptions.reduce((s, o) => s + (o.votes || 0), 0)} oy</span>
                    </div>
                  )}
                  {!isCompact && post.tags?.length > 0 && (
                    <div className="feed-post-tags">{post.tags.map((t, i) => <span key={i} className="feed-tag">#{t}</span>)}</div>
                  )}
                  <PostFileGrid mediaUrls={post.mediaUrls} apiUrl={API_URL} downloadableIndexes={post.downloadableIndexes || []} defaultPreviewIndex={post.defaultPreviewIndex ?? null} postId={post.id} isOwner={post.userId === user.id} token={token} editMode={editingPostId === post.id} autoPreview={isCompact} />
                </>
              )}
              {!isCompact && (
                <>
                  <div className="feed-post-actions">
                    <button className={`feed-action-btn ${post.isLiked ? 'liked' : ''}`} onClick={() => handleLike(post.id)}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill={post.isLiked ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
                      <span>{post.likeCount || 0}</span>
                    </button>
                    <button className="feed-action-btn" onClick={() => toggleComments(post.id)}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                      <span>{post.commentCount || 0}</span>
                    </button>
                    {mode === 'explore' && (
                      <button className="feed-action-btn feed-explore-close" onClick={() => setExpandedPostId(null)} title="Kapat">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                      </button>
                    )}
                  </div>
                  {openComments[post.id] && (
                    <div className="feed-comments">
                      {(postComments[post.id] || []).map(c => (
                        <div className={`feed-comment ${c.isSolution ? 'solution' : ''}`} key={c.id}>
                          <div className="feed-comment-avatar">
                            {c.author?.avatar ? <img src={API_URL + c.author.avatar} alt="" /> : (c.author?.firstName?.[0] || '')}
                          </div>
                          <div className="feed-comment-body">
                            <span className="feed-comment-author">{c.author?.firstName} {c.author?.lastName} {c.isSolution && <span className="feed-solution-mark">Cozum</span>}</span>
                            <span className="feed-comment-text">{c.body}</span>
                          </div>
                          {post.type === 'question' && post.userId === user.id && !c.isSolution && (
                            <button className="feed-mark-solution" onClick={() => handleMarkSolution(post.id, c.id)} title="Cozum olarak isaretle">✓</button>
                          )}
                        </div>
                      ))}
                      <div className="feed-comment-input-row">
                        <input
                          type="text"
                          className="feed-comment-input"
                          placeholder="Yorum yaz..."
                          value={commentTexts[post.id] || ''}
                          onChange={e => setCommentTexts(prev => ({ ...prev, [post.id]: e.target.value }))}
                          onKeyDown={e => { if (e.key === 'Enter') handleComment(post.id); }}
                        />
                        <button className="feed-comment-send" onClick={() => handleComment(post.id)}>Gonder</button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          );})}

          {loading && <div className="feed-loading">Yükleniyor...</div>}
          {!loading && posts.length === 0 && (
            <div className="feed-empty">
              <div className="feed-empty-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
              </div>
              <h3>{activeTab === 'feed' ? 'Aginiz bos' : 'Henuz gonderi yok'}</h3>
              <p>{activeTab === 'feed' ? 'Yukarıdaki arama alanını kullanarak kullanıcıları takip edin.' : 'İlk gönderiyi siz paylaşabilirsiniz.'}</p>
            </div>
          )}
        </div>
      </main>

      {/* RIGHT PANEL */}
      <RightSidebar />
    </div>
  );
};

export { PostFileGrid };
export default Feed;
